/**
 * AuthenticatedImage - Fetches images using Thaklis Bearer token
 * Adapted from Thaklis app to use ThaklisApiService
 */
import React, { useState, useEffect } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { MdPerson } from 'react-icons/md';
import ThaklisApiService from '../../../services/thaklis-api.service';

const resizeImage = (imageUrl, maxSize) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (width > height) {
          if (width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
        } else {
          if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
        }
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = width;
        canvas.height = height;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      } catch (error) { reject(error); }
    };
    img.onerror = () => reject(new Error('Failed to load image for resizing'));
    img.src = imageUrl;
  });
};

const AuthenticatedImage = ({ src, alt = 'Image', employeeId, maxSize, onLoad, onError, style, ...props }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let controller = new AbortController();

    if (!src && !employeeId) {
      setLoading(false);
      setError(true);
      return;
    }

    const fetchImage = async (retryAttempt = 0) => {
      if (!isMounted) return;

      const token = ThaklisApiService.getToken();
      const baseUrl = ThaklisApiService.getBaseUrl();

      if (!token || !baseUrl) {
        setError(true);
        setLoading(false);
        return;
      }

      let processedSrc = src;
      // Fix incorrect data URL prefix
      if (processedSrc && typeof processedSrc === 'string' && processedSrc.startsWith('data:image/jpeg;base64,/v1/users/')) {
        processedSrc = processedSrc.replace('data:image/jpeg;base64,', '');
      }

      try {
        setLoading(true);
        setError(false);

        let fullUrl;
        const effectiveEmployeeId = employeeId || 'unknown';

        if (effectiveEmployeeId !== 'unknown' && (!processedSrc || processedSrc === '' || processedSrc === 'null')) {
          fullUrl = `${baseUrl}/v1/users/${effectiveEmployeeId}/documents/photo/download`;
        } else if (processedSrc && processedSrc.startsWith('/')) {
          fullUrl = `${baseUrl}${processedSrc}`;
        } else if (processedSrc && !processedSrc.startsWith('http')) {
          fullUrl = `${baseUrl}/${processedSrc}`;
        } else if (processedSrc) {
          fullUrl = processedSrc;
        } else {
          setError(true);
          setLoading(false);
          return;
        }

        const timeoutId = setTimeout(() => {
          controller.abort();
          if (retryAttempt < 2 && isMounted) {
            controller = new AbortController();
            fetchImage(retryAttempt + 1);
          } else if (isMounted) {
            setError(true);
            setLoading(false);
          }
        }, 10000);

        const response = await fetch(fullUrl, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` }
        });

        clearTimeout(timeoutId);

        if (!response.ok) throw new Error(`Failed to load image: ${response.status}`);

        const blob = await response.blob();
        if (!blob || blob.size === 0) throw new Error('Empty image data');

        const objectUrl = URL.createObjectURL(blob);

        if (maxSize && isMounted) {
          try {
            const resizedUrl = await resizeImage(objectUrl, maxSize);
            setImageUrl(resizedUrl);
            URL.revokeObjectURL(objectUrl);
          } catch {
            setImageUrl(objectUrl);
          }
        } else if (isMounted) {
          setImageUrl(objectUrl);
        }

        if (isMounted) {
          setLoading(false);
          if (onLoad) onLoad();
        }
      } catch (fetchError) {
        if (retryAttempt < 2 && isMounted) {
          controller = new AbortController();
          setTimeout(() => { if (isMounted) fetchImage(retryAttempt + 1); }, 1000 * (retryAttempt + 1));
        } else if (isMounted) {
          setError(true);
          setLoading(false);
          if (onError) onError(fetchError);
        }
      }
    };

    fetchImage(0);

    return () => {
      isMounted = false;
      controller.abort();
      if (imageUrl && imageUrl.startsWith('blob:')) URL.revokeObjectURL(imageUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, employeeId, maxSize]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" style={style} {...props}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error || !imageUrl) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" style={style} {...props}>
        <MdPerson size={24} />
      </Box>
    );
  }

  return <img src={imageUrl} alt={alt} style={style} {...props} />;
};

export default AuthenticatedImage;
